import { useCallback, useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Hook that provides custom modal for navigation blocking instead of browser-style dialogs
 * @param {boolean} shouldBlock - Whether to block navigation
 */
export const useUnsavedChangesModal = (shouldBlock: boolean) => {
  const [showModal, setShowModal] = useState(false);
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!shouldBlock && blocker.state === 'blocked') {
      blocker.proceed?.();
    }
  }, [blocker, shouldBlock]);

  useEffect(() => {
    if (blocker.state === 'blocked' && shouldBlock) {
      setShowModal(true);
    }
  }, [blocker, shouldBlock]);

  const handleConfirm = useCallback(() => {
    setShowModal(false);
    if (blocker?.proceed) {
      blocker.proceed();
    }
  }, [blocker]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    blocker.reset?.();
  }, [blocker]);

  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  return {
    modalProps: {
      open: showModal,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
};
